import { useQuery } from '@tanstack/react-query';
import { formatarBRL, nomeDoMes, percentual } from '@truefinance/shared';
import { useLocation } from 'wouter';
import { api } from '../api/cliente.js';
import { Carregando, ErroSeguro, Vazio } from '../componentes/estados.js';
import { LinhaTransacao } from '../componentes/Transacao.js';

export function Dashboard() {
  const [, navegar] = useLocation();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.dashboard(),
    refetchInterval: 15_000,
  });

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.categorias(),
  });

  if (isLoading) return <Carregando />;
  if (isError || !data) return <ErroSeguro aoTentarNovamente={() => void refetch()} />;

  const mapaCategorias = new Map(categorias?.categorias.map((c) => [c.id, c.nome]) ?? []);
  const totalSaidas = data.saidasPorCategoria.reduce((soma, c) => soma + c.total, 0);

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">Dashboard</h1>
        <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
          <span className="mono">{nomeDoMes(data.mes.rotulo.ate)}</span> · atualizado a cada 15s
        </p>
      </header>

      {/* KPIs clicáveis — cada um leva à lista já filtrada. */}
      <section className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          rotulo="Entradas do mês"
          valor={formatarBRL(data.mes.entradas)}
          cor="entrada"
          aoClicar={() => navegar('/entradas')}
        />
        <Kpi
          rotulo="Saídas do mês"
          valor={formatarBRL(data.mes.saidas)}
          cor="saida"
          aoClicar={() => navegar('/saidas')}
        />
        <Kpi
          rotulo="Resultado do mês"
          valor={formatarBRL(data.mes.saldo)}
          cor={data.mes.saldo >= 0 ? 'entrada' : 'saida'}
          aoClicar={() => navegar('/transacoes')}
        />
        <Kpi
          rotulo="Pendentes de revisão"
          valor={String(data.pendentesRevisao)}
          cor="pendente"
          destacado={data.pendentesRevisao > 0}
          aoClicar={() => navegar('/revisao')}
        />
      </section>

      <div className="grid gap-4 lg:grid-cols-[1fr_var(--spacing-trilho)]">
        <section className="rounded-[var(--radius-card)] border border-borda bg-superficie shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between border-b border-borda-sutil px-4 py-3">
            <h2 className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
              Últimos lançamentos
            </h2>
            <span className="mono text-[length:var(--text-metadado)] text-texto-terciario">
              {data.lancamentosHoje} hoje
            </span>
          </div>

          {data.ultimasTransacoes.length === 0 ? (
            <div className="p-4">
              <Vazio
                titulo="Nenhum lançamento ainda"
                orientacao="Mande um gasto ou recebimento no seu WhatsApp que ele aparece aqui."
              />
            </div>
          ) : (
            <div>
              {data.ultimasTransacoes.map((t) => (
                <LinhaTransacao
                  key={t.id}
                  transacao={t}
                  categoria={t.categoriaId ? mapaCategorias.get(t.categoriaId) : null}
                  aoClicar={() => navegar('/transacoes')}
                />
              ))}
            </div>
          )}
        </section>

        <div className="flex flex-col gap-4">
          {/* Card escuro de saldo consolidado — mesmo papel do "RESULTADO
              CONSOLIDADO" no handoff do ACP. */}
          <section className="rounded-[var(--radius-card)] bg-card-escuro p-5 text-card-escuro-fg shadow-[var(--shadow-card)]">
            <p className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] opacity-70">
              Saldo consolidado
            </p>
            <p className="mono mt-1 text-[length:var(--text-kpi)] font-semibold">
              {formatarBRL(data.saldoTotal)}
            </p>
            <p className="mt-1 text-[length:var(--text-secundario)] opacity-70">
              Considera apenas lançamentos confirmados
            </p>
          </section>

          <section className="rounded-[var(--radius-card)] border border-borda bg-superficie p-4 shadow-[var(--shadow-card)]">
            <h2 className="mb-3 text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
              Saídas por categoria
            </h2>

            {data.saidasPorCategoria.length === 0 ? (
              <p className="text-[length:var(--text-secundario)] text-texto-secundario">
                Nada gasto neste mês ainda.
              </p>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {data.saidasPorCategoria.slice(0, 6).map((c) => (
                  <li key={c.categoriaId ?? 'sem'}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[length:var(--text-secundario)] text-texto">
                        {c.nome}
                      </span>
                      <span className="mono shrink-0 text-[length:var(--text-secundario)] text-texto-secundario">
                        {formatarBRL(c.total)}
                        <span className="ml-1 text-texto-terciario">
                          ({percentual(c.total, totalSaidas)}%)
                        </span>
                      </span>
                    </div>
                    {/* Barra proporcional. Cor da categoria, largura pelo peso. */}
                    <div className="mt-1 h-1.5 overflow-hidden rounded-[3px] bg-borda-sutil">
                      <div
                        className="h-full rounded-[3px]"
                        style={{
                          width: `${percentual(c.total, totalSaidas)}%`,
                          backgroundColor: c.cor,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  rotulo,
  valor,
  cor,
  destacado = false,
  aoClicar,
}: {
  rotulo: string;
  valor: string;
  cor: 'entrada' | 'saida' | 'pendente' | 'texto';
  destacado?: boolean;
  aoClicar?: () => void;
}) {
  const corTexto = {
    entrada: 'text-entrada',
    saida: 'text-saida',
    pendente: 'text-pendente',
    texto: 'text-texto',
  }[cor];

  return (
    <button
      type="button"
      onClick={aoClicar}
      className={`rounded-[var(--radius-card)] bg-superficie p-4 text-left shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-kpi-hover)] ${
        destacado ? 'border-2 border-pendente' : 'border border-borda'
      }`}
    >
      <p className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
        {rotulo}
      </p>
      <p className={`mono mt-1 text-[length:var(--text-kpi)] font-semibold ${corTexto}`}>{valor}</p>
    </button>
  );
}
